// ═══════════════════════════════════════════════════════════════════════════
// Registrar el pago de una comisión
//
// El operador: "el administrador debe saber qué está pagando... debería poder
// registrarse por remisión y luego al registrar los pagos pueden ser todos los
// que estén consolidados."
//
// Por eso aquí NO SE TECLEA EL MONTO. Se eligen las remisiones que se están
// saldando y la suma es consecuencia. Consolidar sigue siendo lo normal —un
// depósito cubre quince remisiones— pero queda escrito cuáles, y el día que el
// vendedor diga "me faltó la del folio 412" hay con qué contestarle.
//
// El pago a cuenta existe para un anticipo acordado, pero está abajo y se
// escribe aparte: es la excepción, no el camino.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { comisionesPorPagar, pagarComision } from "../datos/vendedores.js";
import { fmt } from "../pdf/comunes.js";
import { Label } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";

const METODOS = ["Transferencia", "Efectivo", "Cheque", "Otro"];

export function PagoComision({ tenantId, persona, onCerrar, onListo }) {
  const [pendientes, setPendientes] = useState([]);
  const [sel, setSel] = useState(() => new Set());
  const [cargando, setCargando] = useState(true);
  const [metodo, setMetodo] = useState("Transferencia");
  const [referencia, setReferencia] = useState("");
  const [nota, setNota] = useState("");
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [aCuenta, setACuenta] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let vivo = true;
    comisionesPorPagar(tenantId, persona.membership_id)
      .then(r => { if (!vivo) return;
        setPendientes(r || []);
        // Todo seleccionado por omisión: lo normal es liquidar lo pendiente.
        setSel(new Set((r || []).map(x => x.remision_id)));
        setCargando(false); })
      .catch(e => { if (vivo) { setError(e.message); setCargando(false); } });
    return () => { vivo = false; };
  }, [tenantId, persona.membership_id]);

  const alternar = (id) => setSel(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const total = pendientes
    .filter(p => sel.has(p.remision_id))
    .reduce((t, p) => t + Number(p.comision), 0);

  const montoACuenta = Number(aCuenta);
  const modoACuenta = sel.size === 0 && Number.isFinite(montoACuenta) && montoACuenta > 0;
  const listo = sel.size > 0 || modoACuenta;

  const guardar = async () => {
    if (!listo) return;
    if (modoACuenta && !confirm(
      `Vas a registrar ${fmt(montoACuenta)} SIN ligarlo a ninguna remisión.\n\n` +
      `Queda como anticipo a cuenta: no marca ninguna comisión como pagada y el ` +
      `saldo por remisión sigue igual. ¿Es lo que quieres?`)) return;

    setOcupado(true); setError("");
    try {
      const r = await pagarComision(tenantId, persona.membership_id, [...sel], {
        montoACuenta: modoACuenta ? montoACuenta : null,
        metodo, referencia: referencia || null, nota: nota || null,
        // La fecha del DEPÓSITO, no la de captura: se registra el martes lo que
        // se transfirió el viernes, y el corte del mes depende de cuál se guardó.
        fecha: new Date(fecha + "T12:00:00").toISOString(),
      });
      onListo?.(r);
    } catch (e) { setError(e.message); setOcupado(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000,
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  overflow: "auto", padding: 16 }}
      onClick={onCerrar}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 12,
                 maxWidth: 620, width: "100%", marginTop: 16, padding: 24 }}>

        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22,
                      color: "#c9a96e", marginBottom: 2 }}>
          Registrar pago de comisión
        </div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
          {persona.nombre || persona.email}
        </div>

        {!persona.tiene_clabe && (
          <div style={{ padding: "8px 12px", background: "#1f1a0d", border: "1px solid #3a3a1a",
                        borderRadius: 6, fontSize: 12, color: "#c9a96e", marginBottom: 14 }}>
            No ha registrado su CLABE. Si ya le transferiste por otra vía, adelante;
            si no, pídesela antes.
          </div>
        )}

        {/* ── Qué se está pagando ────────────────────────────────────── */}
        <div style={{ fontSize: 10, color: "#666", letterSpacing: 1, marginBottom: 6 }}>
          POR ACREDITARSE — el cliente ya pagó estas remisiones
        </div>

        {cargando ? (
          <div style={{ padding: 16, color: "#555", fontSize: 13 }}>Cargando…</div>
        ) : pendientes.length === 0 ? (
          <div style={{ padding: "14px 16px", background: "#0a0a0a", borderRadius: 6,
                        fontSize: 12, color: "#888", lineHeight: 1.7 }}>
            Nada por acreditarse. O el cliente todavía no paga sus remisiones
            —eso queda como <b>acreditada</b>— o ya se le liquidó todo.
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <button onClick={() => setSel(new Set(pendientes.map(p => p.remision_id)))}
                style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11 }}>
                Todas ({pendientes.length})
              </button>
              <button onClick={() => setSel(new Set())}
                style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11 }}>
                Ninguna
              </button>
            </div>

            <div style={{ maxHeight: "34vh", overflowY: "auto", border: "1px solid #1a1a1a",
                          borderRadius: 6, marginBottom: 12 }}>
              {pendientes.map(p => {
                const marcada = sel.has(p.remision_id);
                return (
                  <div key={p.remision_id} onClick={() => alternar(p.remision_id)}
                    style={{ display: "flex", alignItems: "center", gap: 10,
                             padding: "7px 12px", borderBottom: "1px solid #141414",
                             cursor: "pointer", background: marcada ? "#12140d" : "transparent" }}>
                    <input type="checkbox" readOnly checked={marcada}
                      style={{ pointerEvents: "none" }} />
                    <span style={{ fontSize: 12, color: "#c9a96e", fontWeight: 600, minWidth: 44 }}>
                      #{p.folio}
                    </span>
                    <span style={{ fontSize: 11, color: "#666", minWidth: 68 }}>
                      {new Date(p.issued_at).toLocaleDateString("es-MX")}
                    </span>
                    <span style={{ flex: 1, fontSize: 12, color: "#999", overflow: "hidden",
                                   textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.cliente}
                    </span>
                    <span style={{ fontSize: 11, color: "#555" }}>
                      {fmt(Number(p.total))} × {Number(p.pct)}%
                    </span>
                    <span style={{ fontSize: 13, color: "#a8d060", fontWeight: 600, minWidth: 76,
                                   textAlign: "right" }}>
                      {fmt(Number(p.comision))}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── El total, que NO se teclea ─────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
                      background: sel.size ? "#12140d" : "#0a0a0a",
                      border: `1px solid ${sel.size ? "#2a3a1a" : "#1a1a1a"}`,
                      borderRadius: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, fontSize: 12, color: "#888" }}>
            {sel.size > 0
              ? <>{sel.size} remisión{sel.size !== 1 ? "es" : ""} seleccionada{sel.size !== 1 ? "s" : ""}</>
              : <>Nada seleccionado</>}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: sel.size ? "#a8d060" : "#444" }}>
            {fmt(modoACuenta ? montoACuenta : total)}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div>
            <Label>Fecha del depósito</Label>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <Label>Método</Label>
            <select value={metodo} onChange={e => setMetodo(e.target.value)} style={selectStyle}>
              {METODOS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <Label>Referencia</Label>
            {/* El reclamo típico no es "no me pagaste", es "no me llegó". Con el
                folio del SPEI eso se resuelve en un minuto. */}
            <input value={referencia} onChange={e => setReferencia(e.target.value)}
              placeholder="folio del SPEI" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Label>Nota (opcional)</Label>
          <input value={nota} onChange={e => setNota(e.target.value)} style={inputStyle} />
        </div>

        {/* ── La excepción, abajo y aparte ───────────────────────────── */}
        {sel.size === 0 && (
          <div style={{ padding: "10px 12px", background: "#0a0a0a", border: "1px solid #1a1a1a",
                        borderRadius: 6, marginBottom: 12 }}>
            <Label>Anticipo a cuenta (sin ligar a remisiones)</Label>
            <input type="number" step="0.01" value={aCuenta}
              onChange={e => setACuenta(e.target.value)} placeholder="0.00"
              style={{ ...inputStyle, maxWidth: 160 }} />
            <div style={{ fontSize: 10, color: "#666", marginTop: 4, lineHeight: 1.6 }}>
              No marca ninguna comisión como pagada. Úsalo solo para un adelanto acordado.
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: "10px 12px", background: "#1f0d0d", border: "1px solid #3a1a1a",
                        borderRadius: 6, fontSize: 12, color: "#f87171", marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ fontSize: 10, color: "#556", lineHeight: 1.6, marginBottom: 14 }}>
          Queda registrado con las remisiones que cubre, y el vendedor lo ve en su cuenta.
          No se borra: un error se revierte y quedan los dos asientos.
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCerrar} disabled={ocupado} style={{ ...btnSecondary, padding: "9px 18px" }}>
            Cancelar
          </button>
          <button onClick={guardar} disabled={ocupado || !listo}
            style={{ ...btnPrimary, padding: "9px 20px", opacity: (ocupado || !listo) ? 0.4 : 1 }}>
            {ocupado ? "Registrando…" : `Registrar ${fmt(modoACuenta ? montoACuenta : total)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
